import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, Headphones, Keyboard, Laptop, Smartphone, Sparkles, Watch } from "lucide-react";
import { toast } from "sonner";
import { api, fetchProducts } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import BentoFeatured from "@/components/BentoFeatured";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";

const CATEGORY_META = [
  { name: "Smartphones", icon: Smartphone },
  { name: "Audio", icon: Headphones },
  { name: "Laptops", icon: Laptop },
  { name: "Wearables", icon: Watch },
  { name: "Accessories", icon: Keyboard },
];

export default function Home() {
  const { setAssistantOpen } = useCart();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dropEmail, setDropEmail] = useState("");
  const [dropBusy, setDropBusy] = useState(false);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const preview = products.slice(0, 8);

  const subscribe = async () => {
    const email = dropEmail.trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setDropBusy(true);
    try {
      await api.post("/subscribe", { email, origin_url: window.location.origin });
      toast.success("You're on the drop list — check your inbox");
      setDropEmail("");
    } catch {
      toast.error("Signup failed — try again");
    }
    setDropBusy(false);
  };

  return (
    <main>
      <Hero />
      <Marquee />

      {/* Categories strip */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10" data-testid="categories-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
              Pick your <span className="text-voltaic-cyan">frequency</span>
            </h2>
            <Link to="/shop" data-testid="categories-view-all" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-voltaic-muted transition-colors hover:text-voltaic-cyan">
              Full catalogue <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {CATEGORY_META.map((c, i) => {
            const count = products.filter((p) => p.category === c.name).length;
            return (
              <Reveal key={c.name} delay={i * 0.05}>
                <Link
                  to={`/shop?category=${encodeURIComponent(c.name)}`}
                  data-testid={`category-tile-${c.name.toLowerCase()}`}
                  className="card-spotlight group flex flex-col gap-3 rounded-2xl border border-voltaic-line bg-voltaic-surface p-5 transition-colors hover:border-voltaic-cyan/50"
                >
                  <c.icon size={22} className="text-voltaic-cyan" />
                  <div>
                    <p className="font-display font-semibold">{c.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted">{count} devices</p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Bento featured */}
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-10" data-testid="bento-section">
        <Reveal>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">Featured hardware</p>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">This week's heavy current</h2>
        </Reveal>
        <Reveal className="mt-8" delay={0.1}>
          <BentoFeatured products={products} onSelect={setSelected} />
        </Reveal>
      </section>

      {/* Product preview grid */}
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-10" data-testid="home-grid-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">The drop</p>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">Every device, live stock</h2>
            </div>
            <Link to="/shop" data-testid="home-grid-view-all" className="flex items-center gap-1.5 rounded-full border border-voltaic-linebright px-5 py-2.5 text-sm font-semibold transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan">
              View all {products.length || ""} <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {preview.map((p, i) => (
            <ProductCard key={p.id} product={p} onSelect={setSelected} index={i} />
          ))}
        </div>
      </section>

      {/* AI band */}
      <section className="border-y border-voltaic-line bg-voltaic-surface/50" data-testid="ai-band">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-5 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <Reveal>
            <div>
              <p className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">
                <Sparkles size={14} /> Powered by AI
              </p>
              <h2 className="max-w-xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl">
                Not sure what to buy? <span className="text-voltaic-cyan">Ask VOLT.</span>
              </h2>
              <p className="mt-3 max-w-md text-voltaic-muted">
                The AI guru knows every spec, price and stock count in the vault — get a pick in seconds.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              data-testid="ai-band-open-button"
              onClick={() => setAssistantOpen(true)}
              className="flex items-center gap-2 rounded-full bg-voltaic-cyan px-8 py-4 font-semibold text-voltaic-bg transition-shadow duration-300 hover:glow-cyan"
            >
              <Sparkles size={17} /> Launch VOLT AI
            </button>
          </Reveal>
        </div>
      </section>

      {/* Drop alerts / lead capture */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10" data-testid="drop-alerts-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-8 sm:p-12">
            <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-voltaic-cyan/10 blur-[100px]" aria-hidden="true" />
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">
              <Bell size={13} /> Lead the charge
            </p>
            <h2 className="mt-3 max-w-xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl">
              Drop alerts before <span className="text-voltaic-cyan">anyone else</span>
            </h2>
            <p className="mt-3 max-w-md text-voltaic-muted">
              One email per drop: new devices, restocks and member-only codes. No spam, ever.
            </p>
            <div className="mt-7 flex max-w-md flex-col gap-3 sm:flex-row">
              <input
                data-testid="drop-alerts-email"
                type="email"
                value={dropEmail}
                onChange={(e) => setDropEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && subscribe()}
                placeholder="you@email.com"
                className="field flex-1"
              />
              <button
                data-testid="drop-alerts-subscribe"
                onClick={subscribe}
                disabled={dropBusy}
                className="flex items-center justify-center gap-2 rounded-full bg-voltaic-cyan px-7 py-3.5 font-semibold text-voltaic-bg transition-shadow hover:glow-cyan disabled:opacity-50"
              >
                <Bell size={15} /> {dropBusy ? "Joining..." : "Notify me"}
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </main>
  );
}
